import { t } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import { Box, Card, CardContent, Divider, Link, Typography } from '@mui/material'

import { GRTooltip } from '../../../components/GRTooltip/GRTooltip'
import { useCurrentUserLanguage } from '../../account/hooks/useCurrentUserLanguage'
import { GameIcon } from '../../game'
import { getAttributeNameByModel } from '../hooks/useIconAttributes'
import { useVisualAnalysisName } from '../hooks/useVisualAnalysisName'
import { RankType, VisualAnalysisWithRank, VisualsAttribute, VisualsGroupedData, VisualSortType } from '../types'
import { getIconComparator } from '../util/comparators'
import { getVisualAnalysisName } from '../util/helpers'
import { AppStoreGenreMap } from '../util/visualStats'
import './VisualIconsList.scss'

type TooltipProps = {
  attributes: VisualsAttribute[]
  item: VisualAnalysisWithRank
  rankText: string
}

interface VisualIconsListProps {
  attributes: VisualsAttribute[]
  data: VisualsGroupedData
  genreId: string
  rankType: RankType
  onSelectAnalysis: (analysis: VisualAnalysisWithRank) => void
  sortBy: VisualSortType
  top: number
}

type TooltipValueProps = {
  attribute: VisualsAttribute
  item: VisualAnalysisWithRank
}

const TooltipValue: React.FC<TooltipValueProps> = ({ attribute, item }) => {
  const attrName = getAttributeNameByModel(attribute.model)
  const attrs = item[attrName]
  let value: number = (attrs && attrs[attribute.name]) || 0

  // For color groups, `useIconGrouper`'s color-percentage filter accepts a game when either
  // `colorGroups[name]` OR `colors[name]` meets the threshold. Mirror that fallback here so the
  // tooltip stays consistent with the filtered set and we don't leave an orphan divider with no
  // color-group value rendered for games that only carry the value under `colors`. (GR-1752)
  if (value <= 0 && attribute.model === 'colorGroups') {
    const colorsValue = (item.colors && item.colors[attribute.name]) || 0
    if (colorsValue > 0) {
      value = colorsValue
    }
  }

  // Color values from the API may be either a 0-1 fraction or a 0-100 percent.
  // `useIconGrouper` normalizes to 0-100; here we render `value * 100`, so normalize to 0-1
  // first to avoid showing e.g. `5000%` for values returned as 0-100. Only applies to color
  // models since other attribute models are already 0-1 fractions.
  if (attribute.model === 'colorGroups' && value > 1) {
    value = value / 100
  }

  return value <= 0 ? null : (
    <small style={{ display: 'block', textTransform: 'capitalize' }}>
      {attribute.name}: {Math.round(value * 100)}%
    </small>
  )
}

const Tooltip: React.FC<TooltipProps> = ({ attributes, item, rankText }) => {
  // GR-1441: The game name must follow the user's language, not the currently selected market.
  // This restores the earlier partial fix from commit 56117edcb that was reverted by GR-780.
  const gameName = useVisualAnalysisName({ visualAnalysis: item }) || t('common:not_available_shorthand')

  return (
    <>
      <strong>{gameName}</strong>
      <br />
      <small>{rankText}</small>
      {!!attributes?.length && (
        <>
          <Divider sx={{ my: 1 }} />
          {attributes.map((attribute) => (
            <TooltipValue key={attribute.name} attribute={attribute} item={item} />
          ))}
        </>
      )}
    </>
  )
}

const VisualIconsList: React.FC<VisualIconsListProps> = ({ attributes, data, genreId, onSelectAnalysis, rankType, sortBy, top }) => {
  const { t } = useTranslation()
  const comparator = getIconComparator(sortBy, rankType, attributes)
  // GR-1441: Game names must follow the user's language, not the currently selected market.
  const userLanguage = useCurrentUserLanguage()
  return (
    <Box className="VisualIconsList">
      {['top', 'out'].map((group) => {
        const rankText = AppStoreGenreMap[genreId] + ' / ' + t(`common:${rankType === 'free' ? 'sustained_download_rank' : 'sustained_grossing_rank'}`)
        const items = data[group as keyof VisualsGroupedData]
        return (
          <Card key={'group-' + group} sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h3" mb={2}>
                {t('common:' + (group === 'out' ? 'outside_' : '') + 'top_' + top)} {rankText}
              </Typography>
              {!items.length && (
                <Typography mb={4} mt={-1} variant="body2">
                  {t('visuals-explorer:visual_attribute_modal_no_games_found')}
                </Typography>
              )}
              {items.sort(comparator).map((item, index) => {
                const rank = rankType === 'free' ? item.sdrank : item.srank
                return (
                  <Box className="VisualIconsList__gameList" key={index}>
                    <GRTooltip
                      content={<Tooltip item={item} rankText={rankText + ': ' + (rank ?? t('common:not_available_shorthand'))} attributes={attributes} />}
                      key={group + '-icon-' + index}
                    >
                      <LazyLoadComponent>
                        <Link onClick={() => onSelectAnalysis(item)}>
                          <GameIcon gameName={getVisualAnalysisName({ visualAnalysis: item, userLanguage })} src={item.url} />
                        </Link>
                      </LazyLoadComponent>
                    </GRTooltip>
                  </Box>
                )
              })}
            </CardContent>
          </Card>
        )
      })}
    </Box>
  )
}

export default VisualIconsList
